import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pick, sortBy } from '@nullvoxpopuli/ember-composable-helpers';
import { format, parseISO } from 'date-fns';
import { dropTask, restartableTask } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import sortableGroup from 'ember-sortable/modifiers/sortable-group';
import sortableHandle from 'ember-sortable/modifiers/sortable-handle';
import sortableItem from 'ember-sortable/modifiers/sortable-item';
import { eq, gt } from 'ember-truth-helpers';
import type DocumentUploadPageAdapter from 'garaje/adapters/document-upload-page';
import AddModal from 'garaje/components/flows/sections/required-documents/add-modal';
import ManageModal from 'garaje/components/flows/sections/required-documents/manage-modal';
import UiCard from 'garaje/components/ui/card';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiWell from 'garaje/components/ui/well';
import UiWellItem from 'garaje/components/ui/well/item';
import UiPill from 'garaje/components/ui-pill';
import type { ReuploadPolicy } from 'garaje/models/custom-user-document-template';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import { cached, localCopy } from 'tracked-toolbox';

function policyPillLabel(doc: RequiredDocumentSignature): string {
  switch (doc.reuploadPolicy) {
    case 'always':
      return 'Every Visit';
    case 'never':
      return 'No Re-upload Required';
    case 'after_days':
      return `Every ${doc.reuploadDays ?? '365'} Days`;
    case 'on_expiration':
      return 'On Document Expiration';
    case 'on_date_yearly':
      return doc.reuploadDate ? `Yearly on ${format(parseISO(doc.reuploadDate), 'MMM d')}` : 'Yearly on Date';
    default:
      return '';
  }
}

export interface RequiredDocumentSignature {
  id: string;
  title: string;
  subtitle?: string;
  policyName?: string;
  reuploadPolicy?: ReuploadPolicy;
  reuploadDays?: string;
  reuploadDate?: string; // ISO date string
  position: number;
  isSaving?: boolean;
  configurationId?: string;
  allowLlmParsing?: boolean;
}

interface FlowsSectionsRequiredDocumentsListComponentSignature {
  Args: {
    documents: RequiredDocumentSignature[];
    documentUploadPageId?: string;
    isDisabled?: boolean;
    isEnabled?: boolean;
    onDeleteDocument?: (document: RequiredDocumentSignature) => void;
    onDocumentsChanged?: (change?: {
      id: string;
      action: 'added' | 'removed';
      configuration?: UserDocumentTemplateConfigurationModel;
    }) => void;
    onEditDocument?: (document: RequiredDocumentSignature) => void;
    onReorderDocuments?: (documents: RequiredDocumentSignature[]) => Promise<void>;
    onToggleEnabled?: (value: boolean) => void;
  };
}

export default class FlowsSectionsRequiredDocumentsListComponent extends Component<FlowsSectionsRequiredDocumentsListComponentSignature> {
  @service declare store: Store;
  @service declare logger: LoggerService;
  @service declare flashMessages: FlashMessagesService;

  @localCopy('args.isEnabled', false) declare isEnabled: boolean;
  @tracked showingAddModal = false;
  @tracked showingManageModal = false;
  @tracked editingDocument: RequiredDocumentSignature | undefined = undefined;
  @tracked _documentToDelete: RequiredDocumentSignature | null = null;
  @tracked _newDocuments: RequiredDocumentSignature[] = [];

  get isShowingDeleteConfirmation(): boolean {
    return !!this._documentToDelete;
  }

  @cached
  get allDocuments(): RequiredDocumentSignature[] {
    // _newDocuments takes precedence: dedup any doc already reflected in args.documents
    // (the store reload in add-modal makes args.documents the source of truth after save)
    const newDocIds = new Set(this._newDocuments.map((d) => d.id));
    const fromArgs = this.args.documents.filter((d) => !newDocIds.has(d.id));
    return [...fromArgs, ...this._newDocuments];
  }

  @cached
  get existingDocumentIds(): string[] {
    return this.allDocuments.map((d) => d.id);
  }

  get isReorderDisabled(): boolean {
    // Disable (not just hide the handle): ember-sortable drags the whole card when no handle is rendered.
    return Boolean(this.args.isDisabled) || this.allDocuments.length <= 1;
  }

  updateDocumentPositions = restartableTask(async (documents: RequiredDocumentSignature[]) => {
    // Assign new 1-indexed positions based on the reordered array
    const reordered = documents.map((doc, index) => ({ ...doc, position: index + 1 }));

    // Identify the dragged item: it has the largest position delta.
    // Shifted siblings always move by exactly 1; the dragged item moves by >= 1.
    // For single-position swaps (delta=1 for both), either item produces the
    // same backend result, so picking the max is always correct.
    const oldPositionById: Record<string, number> = {};
    for (const d of this.allDocuments) {
      oldPositionById[d.id] = d.position;
    }
    let movedDoc: (typeof reordered)[number] | null = null;
    let maxDiff = 0;
    for (const doc of reordered) {
      const oldPos = oldPositionById[doc.id] ?? 0;
      const diff = Math.abs(oldPos - doc.position);
      if (diff > maxDiff) {
        maxDiff = diff;
        movedDoc = doc;
      }
    }

    // Update local state immediately for responsive UI
    this._newDocuments = reordered;

    if (this.args.onReorderDocuments) {
      await this.args.onReorderDocuments(reordered);
    }

    // Persist the moved item's new position to the backend. The backend's
    // Positionable concern automatically shifts all sibling positions, so
    // only ONE request is needed.
    if (this.args.documentUploadPageId && movedDoc) {
      const adapter = this.store.adapterFor('document-upload-page') as DocumentUploadPageAdapter;
      try {
        await adapter.updateTemplate(this.args.documentUploadPageId, movedDoc.id, {
          position: movedDoc.position,
        });
      } catch {
        this.logger.warn('[RequiredDocumentsList] Position update failed');
      }
    }
  });

  toggleEnabled = (value: boolean): void => {
    this.isEnabled = value;
    this.args.onToggleEnabled?.(value);
  };

  openAddModal = (): void => {
    this.showingAddModal = true;
  };

  closeAddModal = (): void => {
    this.showingAddModal = false;
    this.editingDocument = undefined;
  };

  openManageModal = (): void => {
    this.showingAddModal = false;
    this.editingDocument = undefined;
    // Delay opening the manage modal until the add-modal fully unmounts.
    // 150ms matches modal fade-out transition duration.
    setTimeout(() => {
      if (!this.isDestroyed && !this.isDestroying) {
        this.showingManageModal = true;
      }
    }, 150);
  };

  closeManageModal = (): void => {
    this.showingManageModal = false;
    // Templates may have been edited/deleted — notify parent to refresh
    this.args.onDocumentsChanged?.();
  };

  handleDocumentSaved = (document: RequiredDocumentSignature): void => {
    if (this.editingDocument) {
      // If the template was swapped during edit (different ID), remove the old one
      const swapped = this.editingDocument.id !== document.id;
      if (swapped) {
        this._newDocuments = this._newDocuments.filter((d) => d.id !== this.editingDocument!.id);
        this.args.onDocumentsChanged?.({ id: this.editingDocument.id, action: 'removed' });
      }

      const existsInNew = this._newDocuments.some((d) => d.id === document.id);
      if (existsInNew) {
        this._newDocuments = this._newDocuments.map((d) =>
          d.id === document.id ? { ...document, position: d.position } : d,
        );
      } else {
        // Document is new or came from args.documents — add to local state
        const existing = this.editingDocument;
        this._newDocuments = [
          ...this._newDocuments,
          { ...document, position: existing?.position ?? this.allDocuments.length + 1 },
        ];
      }
    } else {
      this._newDocuments = [...this._newDocuments, { ...document, position: this.allDocuments.length + 1 }];
    }
    // Notify the parent controller with the template ID and configuration so it can track added
    // documents. This ensures documents and their configurations survive component destruction.
    const configuration = document.configurationId
      ? this.store.peekRecord('user-document-template-configuration', document.configurationId) ?? undefined
      : undefined;
    this.args.onDocumentsChanged?.({ id: document.id, action: 'added', configuration });
  };

  editDocument = (document: RequiredDocumentSignature): void => {
    this.editingDocument = document;
    this.showingAddModal = true;
  };

  confirmDeleteDocument = (document: RequiredDocumentSignature): void => {
    this._documentToDelete = document;
  };

  cancelDelete = (): void => {
    this._documentToDelete = null;
  };

  removeDocument = dropTask(async () => {
    const document = this._documentToDelete;
    if (!document) return;

    try {
      if (this.args.documentUploadPageId) {
        await this.store
          .adapterFor('document-upload-page')
          .removeFromRelationship(
            'document-upload-page',
            this.args.documentUploadPageId,
            'custom-user-document-templates',
            [{ type: 'custom-user-document-templates', id: document.id }],
          );
        // No reload needed — parent controller's onDeleteDocument handles
        // _configurations state update, and the store relationship is already
        // removed by the adapter call above.
      }

      if (this.args.onDeleteDocument) {
        this.args.onDeleteDocument(document);
      } else {
        this._newDocuments = this._newDocuments.filter((d) => d.id !== document.id);
      }

      this.args.onDocumentsChanged?.({ id: document.id, action: 'removed' });
      this.flashMessages.showAndHideFlash('success', 'Document removed.');
    } catch (e) {
      this.logger.error('[RequiredDocumentsList] Failed to remove document', e as Error);
      this.flashMessages.showAndHideFlash('error', 'Something went wrong. Please try again.');
    } finally {
      this._documentToDelete = null;
    }
  });

  <template>
    <SettingsPanel class="p-0 bg-carbon-5" data-test-required-documents-section as |panel|>
      <panel.header
        @iconUri="/assets/images/features/signature.svg"
        @title="Required document uploads"
        @description="Require visitors to upload specific documents before they can check in."
        class="p-4"
        as |header|
      >
        <header.controls>
          <div class="bg-white">
            <label class="toggle">
              <input
                type="checkbox"
                checked={{this.isEnabled}}
                {{on "change" (pick "target.checked" this.toggleEnabled)}}
                disabled={{@isDisabled}}
                data-test-feature-toggle
                aria-label="Enable Feature"
              />
              <span></span>
            </label>
          </div>
        </header.controls>
      </panel.header>

      <panel.body class="bg-carbon-5" @isOpen={{this.isEnabled}}>
        <div class="relative z-10 -mx-px py-3 shadow-md shadow-gray-50">
          <div class="px-4 flex items-center justify-between">
            <span class="text-sm font-normal leading-[21px] text-carbon-60" data-test-document-count>
              {{this.allDocuments.length}}
              {{if (eq this.allDocuments.length 1) "document" "documents"}}
              required
            </span>
            {{#unless @isDisabled}}
              <button
                type="button"
                class="bg-white border border-carbon-40 rounded-md px-4 py-2.5 text-base font-medium text-carbon-70 hover:bg-carbon-5 transition-colors self-start"
                data-test-add-new-document-button="true"
                {{on "click" this.openAddModal}}
              >
                New document
              </button>
            {{/unless}}
          </div>
        </div>

        <UiWell
          class="!mx-0 !p-4 mt-4"
          data-test-required-documents-list
          {{sortableGroup onChange=(perform this.updateDocumentPositions)}}
        >
          <div class="-mb-4">
            {{#each (sortBy "position" this.allDocuments) as |document|}}
              <UiWellItem
                data-test-sortable-required-document
                data-test-id={{document.id}}
                class="mb-4 trans-all"
                {{sortableItem model=document disabled=this.isReorderDisabled}}
              >
                <UiCard class="is-dragging:shadow-lg-tall trans-box-shadow w-full" style="max-width: none">
                  <div class="flex space-x-4 items-center justify-between">
                    <div class="flex-auto flex flex-col space-y-1 min-w-0">
                      <h4 class="mb-0 truncate">{{document.title}}</h4>
                      {{#if document.subtitle}}
                        <p class="text-sm text-carbon-50 mb-0 truncate">{{document.subtitle}}</p>
                      {{/if}}
                    </div>

                    <div class="flex items-center space-x-2 shrink-0">
                      {{#if document.reuploadPolicy}}
                        <UiPill @fill="newLightBlue" class="!ml-0 mr-1" data-test-policy-pill>
                          {{policyPillLabel document}}
                        </UiPill>
                      {{/if}}
                      <button
                        type="button"
                        class="btn btn--small btn--brand"
                        {{on "click" (fn this.editDocument document)}}
                        data-test-edit-document
                      >
                        {{if @isDisabled "View" "Edit"}}
                      </button>
                    </div>
                  </div>
                </UiCard>
                {{#unless @isDisabled}}
                  <div class="flex items-center">
                    <div class="flex-1">
                      {{#if (gt this.allDocuments.length 1)}}
                        <div
                          class="icon-reSort
                            {{if
                              document.isSaving
                              'icon-reSort-disabled'
                              'handle cursor-row-resize hover:cursor-row-resize'
                            }}"
                          {{sortableHandle}}
                        />
                      {{/if}}
                    </div>
                    <button
                      type="button"
                      class="btn btn--small btn--square btn--hoverFill ml-2"
                      {{on "click" (fn this.confirmDeleteDocument document)}}
                      aria-label="Delete document"
                      data-test-delete-document
                    >
                      <span class="icon-trash"></span>
                    </button>
                  </div>
                {{/unless}}
              </UiWellItem>
            {{/each}}
          </div>
        </UiWell>

        <p class="-mx-px p-4 pt-0 text-sm mb-0">
          Learn more about adding and configuring
          {{! TODO(VIS-15682): Update URL once dedicated "required document uploads" help page is created }}
          <a href="https://envoy.help/en/collections/1930712-visitors" target="_blank" rel="noopener">required document
            uploads</a>.
        </p>

      </panel.body>
    </SettingsPanel>

    {{#if this.showingAddModal}}
      <AddModal
        @documentUploadPageId={{@documentUploadPageId}}
        @existingDocument={{this.editingDocument}}
        @existingDocumentIds={{this.existingDocumentIds}}
        @onClose={{this.closeAddModal}}
        @onOpenManage={{unless @isDisabled this.openManageModal}}
        @onSaved={{this.handleDocumentSaved}}
      />
    {{/if}}

    {{#if this.showingManageModal}}
      <ManageModal @onClose={{this.closeManageModal}} />
    {{/if}}

    {{#if this.isShowingDeleteConfirmation}}
      <ConfirmationModal
        @title="Remove document?"
        @ctaText="Remove document"
        @handleTaskAction={{this.removeDocument}}
        @onClose={{this.cancelDelete}}
        @closeButtonClass="btn--outlineFill"
      >
        <p class="mt-4 mb-0 text-base text-carbon-60">
          Visitors will no longer be required to upload
          <strong class="text-carbon-70">{{this._documentToDelete.title}}</strong>
          when signing in. You can add it back later.
        </p>
      </ConfirmationModal>
    {{/if}}
  </template>
}
